<template>
  <div
    v-show="this.$store.state.theme.layout === 'default'"
    class="min-height-300 position-absolute w-100"
    :class="`${this.$store.state.theme.darkMode ? 'bg-transparent' : 'bg-success'}`"
  />
  <aside
    class="border-0 sidenav navbar navbar-vertical navbar-expand-xs"
    :class="[
      this.$store.state.theme.isRTL ? 'me-3 rotate-caret fixed-end' : 'fixed-start',
      this.$store.state.theme.layout === 'landing' ? 'bg-transparent shadow-none' : '',
      { 'sidebar-is-collapsed': collapsed }
    ]"
    id="sidenav-main"
  >
    <button class="sidebar-collapse-btn" @click="toggleCollapse" :title="collapsed ? $t('General.Expand') : $t('General.Collapse')">
      <i class="fas fa-chevron-left sidebar-collapse-icon"></i>
    </button>

    <div class="w-100 sidebar-top-header" ref="sidebarHeader">

      <div v-if="!$store.getters['init/getHasDemobot'] && Object.keys($store.state.init.user_bots).length" class="bot-selector-wrap">
        <a class="sidebar-bot-header" href="#" @click.prevent="toggleBotDropdown" ref="botTrigger">
          <span v-if="currentBot" class="bot-header-inner">

            <!-- Avatar 36×36 -->
            <div
              class="avatar-box avatar-box--sm"
              :data-state="avatarStatus[currentBot.bot_name] || 'loading'"
              :style="{ backgroundImage: 'url(' + noAvatarImg + ')' }"
            >
              <img
                :src="avatarUrl(currentBot.bot_name)"
                class="avatar-img"
                alt=""
                loading="lazy"
                @load="setAvatarStatus(currentBot.bot_name, 'loaded')"
                @error="setAvatarStatus(currentBot.bot_name, 'error')"
              />
            </div>

            <span class="sidebar-bot-info">
              <span class="sidebar-bot-name">
                {{ currentBot.bot_name }}
                <el-tooltip v-if="!currentBot.status" :content="$t('General.Bots.BotNotConnected')" popper-style="width: 300px" raw-content>
                  <i class="fas fa-exclamation-triangle text-warning ms-1" style="font-size:11px;"></i>
                </el-tooltip>
              </span>
              <span v-if="currentBot.project_name" class="sidebar-bot-project">{{ currentBot.project_name }}</span>
            </span>
            <i class="fas fa-chevron-down sidebar-bot-caret" :class="{ 'rotated': botDropdownOpen }"></i>
          </span>
        </a>

        <!-- Fixed-position dropdown panel -->
        <teleport to="body">
          <div
            v-if="botDropdownOpen"
            class="bot-selector-panel"
            :style="botPanelStyle"
            ref="botPanel"
          >
            <div v-if="allBotsCount > BOT_SEARCH_THRESHOLD" class="bot-selector-search">
              <i class="fas fa-search bot-search-icon"></i>
              <input
                ref="botSearchInput"
                v-model="botSearch"
                class="bot-search-input"
                :placeholder="$t('General.Bots.SearchPlaceholder')"
                type="text"
                autocomplete="off"
              />
              <button v-if="botSearch" class="bot-search-clear" @click="botSearch = ''">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <div class="bot-selector-list">
              <div
                v-for="item in visibleBots"
                :key="item.id"
                class="bot-selector-item"
                :class="{ 'is-active': $store.state.init.current_bot?.key === item.key }"
                @click="selectBot(item)"
              >
                <!-- Avatar 40×40 -->
                <div
                  class="avatar-box avatar-box--md"
                  :data-state="avatarStatus[item.bot_name] || 'loading'"
                  :style="{ backgroundImage: 'url(' + noAvatarImg + ')' }"
                >
                  <img
                    :src="avatarUrl(item.bot_name)"
                    class="avatar-img"
                    alt=""
                    loading="lazy"
                    @load="setAvatarStatus(item.bot_name, 'loaded')"
                    @error="setAvatarStatus(item.bot_name, 'error')"
                  />
                </div>

                <span class="bot-item-name">@{{ item.bot_name }}</span>

                <span class="bot-item-badges">
                  <el-tooltip v-if="!item.status" :content="$t('General.Bots.BotNotConnected')" popper-style="width: 260px" raw-content>
                    <i class="fas fa-exclamation-triangle text-warning"></i>
                  </el-tooltip>
                  <i v-if="$store.state.init.current_bot?.key === item.key" class="fas fa-check bot-item-check"></i>
                </span>
              </div>

              <div v-if="hiddenBotsCount > 0" class="bot-selector-hint">
                {{ $t('General.Bots.SearchHint', { count: hiddenBotsCount }) }}
              </div>
            </div>

            <div class="bot-selector-footer">
              <router-link to="/resorces" class="bot-selector-add" @click="botDropdownOpen = false">
                <i class="fas fa-plus"></i>
                <span>{{ $t("General.Bots.AddResource") }}</span>
              </router-link>
            </div>
          </div>
        </teleport>
      </div>

      <div v-else>
        <router-link to="/resorces/connect" class="sidebar-add-bot">
          <i class="fas fa-plus me-1"></i>{{ $t("General.Bots.AddResource") }}
        </router-link>
      </div>
    </div>

    <div class="sidebar-scroll-wrapper">
      <el-scrollbar
        class="sidebar-nav-scrollbar"
        ref="sidenavScrollbar"
        :style="navScrollbarStyle"
        @scroll="onNavScroll"
      >
        <sidenav-list :cardBg="custom_class" :collapsed="collapsed" />
      </el-scrollbar>
      <div class="sidebar-scroll-fade" v-show="showScrollFade"></div>
    </div>

    <sidenav-announcement v-if="!collapsed" ref="sidebarAnnouncement" @loaded="updateScrollbarHeight" />

    <div class="sidebar-feedback-wrap" ref="sidebarFeedback">
      <button class="sidebar-feedback-btn" :title="$t('Feedback.ButtonShort')" @click="feedbackOpen = true">
        <i class="fas fa-comment-dots"></i>
        <span v-if="!collapsed" class="sidebar-feedback-text">{{ $t('Feedback.ButtonShort') }}</span>
      </button>
    </div>

    <FeedbackModal v-if="feedbackOpen" source="sidebar" @close="feedbackOpen = false" />
  </aside>
</template>

<script>
import SidenavList from "./SidenavList.vue";
import SidenavAnnouncement from "./SidenavAnnouncement.vue";
import FeedbackModal from "@/components/FeedbackModal.vue";
import logo from "@/assets/img/logo-dark.png";
import logoWhite from "@/assets/img/logo-white.png";
import noAvatarImg from "@/assets/img/chat/no_avatar.png";

export default {
  name: "index",
  components: { SidenavList, SidenavAnnouncement, FeedbackModal },
  props: ["custom_class", "layout"],

  data() {
    return {
      logo,
      logoWhite,
      noAvatarImg,
      botDropdownOpen: false,
      botPanelStyle: {},
      avatarStatus: {},
      showScrollFade: false,
      scrollbarHeight: 0,
      botSearch: '',
      BOT_SEARCH_THRESHOLD: 10,
      BOT_VISIBLE_LIMIT: 30,
      feedbackOpen: false,
    };
  },

  computed: {
    collapsed() {
      return this.$store.state.theme.sidebarCollapsed;
    },
    currentBot() {
      const key = this.$store.state.init.current_bot?.key;
      return key ? this.$store.state.init.user_bots[key] : null;
    },
    navScrollbarStyle() {
      return this.scrollbarHeight > 0 ? { height: this.scrollbarHeight + 'px' } : {};
    },
    allBotsCount() {
      return Object.keys(this.$store.state.init.user_bots).length;
    },
    filteredBots() {
      const bots = Object.values(this.$store.state.init.user_bots);
      const q = this.botSearch.trim().toLowerCase();
      if (!q) return bots;
      return bots.filter(b => b.bot_name.toLowerCase().includes(q));
    },
    visibleBots() {
      return this.filteredBots.slice(0, this.BOT_VISIBLE_LIMIT);
    },
    hiddenBotsCount() {
      return Math.max(0, this.filteredBots.length - this.BOT_VISIBLE_LIMIT);
    },
  },

  methods: {
    avatarUrl(botName) {
      return `https://t.me/i/userpic/320/${encodeURIComponent(botName)}.jpg`;
    },

    setAvatarStatus(botName, status) {
      if (!this._alive) return;
      this.avatarStatus = { ...this.avatarStatus, [botName]: status };
    },

    toggleCollapse() {
      this.$store.commit('theme/toggleSidebarCollapse');
    },

    toggleBotDropdown() {
      if (this.botDropdownOpen) {
        this.botDropdownOpen = false;
        this.botSearch = '';
        return;
      }
      this.updatePanelPosition();
      this.botDropdownOpen = true;
      if (this.allBotsCount > this.BOT_SEARCH_THRESHOLD) {
        this.$nextTick(() => this.$refs.botSearchInput?.focus());
      }
    },

    updatePanelPosition() {
      const trigger = this.$refs.botTrigger;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const panelW = Math.min(480, window.innerWidth - 16);
      if (this.collapsed) {
        const left = Math.min(rect.right + 6, window.innerWidth - panelW - 8);
        this.botPanelStyle = { top: rect.top + 'px', left: left + 'px' };
      } else {
        const left = Math.min(rect.left, window.innerWidth - panelW - 8);
        this.botPanelStyle = { top: rect.bottom + 4 + 'px', left: left + 'px' };
      }
    },

    selectBot(item) {
      this.$store.commit('init/setCurrentBot', item);
      this.botDropdownOpen = false;
      this.botSearch = '';
    },

    handleClickOutside(e) {
      const panel = this.$refs.botPanel;
      const trigger = this.$refs.botTrigger;
      if (
        this.botDropdownOpen &&
        panel && !panel.contains(e.target) &&
        trigger && !trigger.contains(e.target)
      ) {
        this.botDropdownOpen = false;
        this.botSearch = '';
      }
    },

    onNavScroll({ scrollTop }) {
      const el = this.$refs.sidenavScrollbar?.wrapRef;
      if (!el) return;
      const atBottom = el.scrollHeight - scrollTop - el.clientHeight < 10;
      this.showScrollFade = !atBottom;
    },

    checkScrollFade() {
      const el = this.$refs.sidenavScrollbar?.wrapRef;
      if (el) {
        this.showScrollFade = el.scrollHeight > el.clientHeight + 10;
      }
    },

    updateScrollbarHeight() {
      const aside = document.getElementById('sidenav-main');
      const header = this.$refs.sidebarHeader;
      const feedback = this.$refs.sidebarFeedback;
      const announcement = this.$refs.sidebarAnnouncement?.$el;
      if (!aside || !header) return;
      const announcementHeight = announcement?.nodeType === 1 ? announcement.offsetHeight : 0;
      const h = aside.offsetHeight - header.offsetHeight - (feedback?.offsetHeight || 0) - announcementHeight;
      this.scrollbarHeight = Math.max(0, h);
      this.$nextTick(this.checkScrollFade);
    }
  },

  mounted() {
    this._alive = true;
    document.addEventListener('click', this.handleClickOutside, true);
    this.$nextTick(() => {
      this.updateScrollbarHeight();
      // Следим за изменениями размеров (ресайз окна, смена бота)
      this._sidebarRO = new ResizeObserver(this.updateScrollbarHeight);
      const aside = document.getElementById('sidenav-main');
      const header = this.$refs.sidebarHeader;
      const feedback = this.$refs.sidebarFeedback;
      if (aside)    this._sidebarRO.observe(aside);
      if (header)   this._sidebarRO.observe(header);
      if (feedback) this._sidebarRO.observe(feedback);
    });
  },
  beforeUnmount() {
    this._alive = false;
    document.removeEventListener('click', this.handleClickOutside, true);
    if (this._sidebarRO) this._sidebarRO.disconnect();
  }
};
</script>

<style>
/* ── Sidebar layout ───────────────────────────────────────────────── */
#sidenav-main {
  overflow: hidden !important;
}

/* Обёртка под хедером — позиционирует fade */
#sidenav-main .sidebar-scroll-wrapper {
  position: relative;
}

/* Внутренний wrap — без горизонтального скролла */
#sidenav-main .sidebar-nav-scrollbar .el-scrollbar__wrap {
  overflow-x: hidden !important;
}

/* ── Scrollbar track & thumb ──────────────────────────────────────── */
#sidenav-main .el-scrollbar__bar.is-vertical {
  width: 4px;
  right: 2px;
}

/* Горизонтальный bar скрываем — он не нужен */
#sidenav-main .el-scrollbar__bar.is-horizontal {
  display: none !important;
}

#sidenav-main .el-scrollbar__thumb {
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.18);
  transition: background-color 0.2s;
}
#sidenav-main .el-scrollbar__bar:hover .el-scrollbar__thumb {
  background-color: rgba(0, 0, 0, 0.32);
}

/* ── Feedback button (sidebar bottom) ─────────────────────────────── */
.sidebar-feedback-wrap {
  flex-shrink: 0;
  padding: 10px 16px 14px;
}
.sidebar-feedback-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 10px;
  background: var(--el-color-primary, #5e72e4);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(94, 114, 228, 0.35);
  transition: filter 0.15s, transform 0.1s;
}
.sidebar-feedback-btn:hover {
  filter: brightness(1.08);
}
.sidebar-feedback-btn:active {
  transform: translateY(1px);
}
.sidebar-feedback-btn i { font-size: 14px; }
.sidebar-is-collapsed .sidebar-feedback-btn {
  padding: 9px;
}
.sidebar-feedback-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Gradient fade — подсказка что контент продолжается ───────────── */
.sidebar-scroll-fade {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 52px;
  background: linear-gradient(to bottom, transparent, var(--sidebar-bg, #fff));
  pointer-events: none;
  transition: opacity 0.25s ease;
}

/* ── Bot selector header ────────────────────────────────────────── */
.bot-selector-wrap { width: 100%; }

.bot-header-inner {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-width: 0;
}

/* ── Avatar box — работает через data-state ─────────────────────── */
.avatar-box {
  position: relative;
  flex-shrink: 0;
  border-radius: 9px;
  overflow: hidden;
  /* no_avatar.png всегда как подложка; реальное фото накрывает сверху */
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.avatar-box--sm { width: 36px; height: 36px; }
.avatar-box--md { width: 40px; height: 40px; border-radius: 10px; }

/* img невидим пока грузится; opacity: 0 прозрачен — подложка видна насквозь */
.avatar-box .avatar-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.15s;
}

/* loaded: t.me-фото загрузилось — накрывает подложку */
.avatar-box[data-state="loaded"] .avatar-img { opacity: 1; }

/* error: убираем сломанный img из потока — подложка остаётся */
.avatar-box[data-state="error"] .avatar-img { display: none; }

/* ── Bot selector panel ─────────────────────────────────────────── */
.bot-selector-panel {
  position: fixed;
  z-index: 2000;
  background: #fff;
  border: 1px solid var(--c-gray-200);
  border-radius: 14px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.07);
  width: min(480px, calc(100vw - 16px));
  overflow: hidden;
  animation: bot-panel-in 0.15s ease;
}

@keyframes bot-panel-in {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
}

.bot-selector-list {
  max-height: min(560px, calc(100vh - 140px));
  overflow-y: auto;
  padding: 8px;
}

.bot-selector-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.12s;
}
.bot-selector-item:hover    { background: var(--c-gray-100); }
.bot-selector-item.is-active { background: var(--c-primary-50); }

.bot-item-name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--c-gray-800);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bot-item-badges {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.bot-item-check {
  color: var(--c-primary-600);
  font-size: 11px;
}

.bot-selector-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--c-gray-100);
}

.bot-search-icon {
  color: var(--c-gray-400);
  font-size: 13px;
  flex-shrink: 0;
}

.bot-search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;
  color: var(--c-gray-800);
}

.bot-search-input::placeholder { color: var(--c-gray-400); }

.bot-search-clear {
  background: none;
  border: none;
  padding: 0;
  color: var(--c-gray-400);
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
}
.bot-search-clear:hover { color: var(--c-gray-600); }

.bot-selector-hint {
  padding: 8px 12px;
  font-size: 12px;
  color: var(--c-gray-400);
  text-align: center;
}

.bot-selector-footer {
  border-top: 1px solid var(--c-gray-100);
  padding: 6px;
}

.bot-selector-add {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: var(--c-primary-600);
  text-decoration: none !important;
  cursor: pointer;
  transition: background 0.12s;
}
.bot-selector-add:hover { background: var(--c-primary-50); }

/* caret rotation */
.sidebar-bot-caret { transition: transform 0.2s ease; }
.sidebar-bot-caret.rotated { transform: rotate(180deg); }
</style>

<style>
/* ── Тёмная тема: скроллбар ───────────────────────────────────────── */
.dark-version #sidenav-main .el-scrollbar__thumb {
  background-color: rgba(255, 255, 255, 0.18);
}
.dark-version #sidenav-main .el-scrollbar__bar:hover .el-scrollbar__thumb {
  background-color: rgba(255, 255, 255, 0.35);
}

.dark-version .bot-selector-panel {
  background: var(--ui-bg-card);
  border-color: var(--ui-border);
  box-shadow: 0 8px 40px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3);
}
.dark-version .bot-selector-item:hover {
  background: rgba(255,255,255,0.06);
}
.dark-version .bot-selector-item.is-active {
  background: var(--c-primary-bg);
}
.dark-version .bot-item-name {
  color: var(--ui-text-1);
}
.dark-version .bot-selector-search {
  border-bottom-color: var(--ui-border);
}
.dark-version .bot-search-input {
  color: var(--ui-text-1);
}
.dark-version .bot-selector-footer {
  border-top-color: var(--ui-border);
}
.dark-version .bot-selector-add {
  color: var(--c-primary-fg);
}
.dark-version .bot-selector-add:hover {
  background: var(--c-primary-bg);
}
</style>
